import { normalizeErrorString } from '@medplum/core'
import { useMedplum } from '@medplum/react'
import { showNotification } from '@mantine/notifications'
import { IconCircleOff } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { SNOMED_USER_ROLES } from '../utils'

export type RoleCheckStatus = 'loading' | 'ready' | 'error'

type CallCenterManagerRoleState = {
  status: RoleCheckStatus
  isCallCenterManager: boolean
}

export function useCallCenterManagerRole(profileId?: string): CallCenterManagerRoleState {
  const medplum = useMedplum()
  const [state, setState] = useState<CallCenterManagerRoleState>({
    status: 'loading',
    isCallCenterManager: false,
  })

  useEffect(() => {
    let cancelled = false

    async function checkRole(): Promise<void> {
      if (!profileId || medplum.isSuperAdmin()) {
        setState({ status: 'ready', isCallCenterManager: false })
        return
      }

      setState((prev) =>
        prev.status === 'ready' ? prev : { status: 'loading', isCallCenterManager: false },
      )

      try {
        const assignedRoles = await medplum.searchOne(
          'PractitionerRole',
          `practitioner=Practitioner/${profileId}`,
        )
        const snomedRoleCode = SNOMED_USER_ROLES.CC_MANAGER.code
        const userIsCallCenterManager =
          assignedRoles?.code?.some((item) =>
            item.coding?.some((coding) => coding.code === snomedRoleCode),
          ) ?? false

        if (!cancelled) {
          setState({ status: 'ready', isCallCenterManager: userIsCallCenterManager })
        }
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'error', isCallCenterManager: false })
          showNotification({
            color: 'red',
            icon: <IconCircleOff />,
            title: 'Error',
            message: normalizeErrorString(err),
          })
        }
      }
    }

    void checkRole()

    return () => {
      cancelled = true
    }
  }, [medplum, profileId])

  return state
}
